/**
 * bundle-builder.js
 * Vanilla ES6 web component for Mackly "Build Your Pack" bundle builder.
 * No external dependencies. Mobile-first, INR currency.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Sort size strings logically.
   * Handles age ranges like "2-4 Yrs", "4-6 Yrs", numeric sizes "S/M/L/XL",
   * and plain strings as fallback.
   */
  function sortSizes(sizes) {
    var ageRangePattern = /^(\d+)\s*[-–]\s*(\d+)/;
    var singleAgePattern = /^(\d+)\s*(yr|yrs|year|years|m|months|month)/i;
    var numericPattern = /^(\d+)$/;
    var sizeOrder = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', '3xl'];

    function parseWeight(size) {
      var s = size.trim();
      var ageMatch = ageRangePattern.exec(s);
      if (ageMatch) {
        return parseFloat(ageMatch[1]) + parseFloat(ageMatch[2]) * 0.001;
      }
      var singleMatch = singleAgePattern.exec(s);
      if (singleMatch) {
        return parseFloat(singleMatch[1]) * 0.1; // months/years below age ranges
      }
      var numMatch = numericPattern.exec(s);
      if (numMatch) {
        return parseFloat(numMatch[1]);
      }
      var sizeIdx = sizeOrder.indexOf(s.toLowerCase());
      if (sizeIdx !== -1) {
        return 1000 + sizeIdx;
      }
      return 9999;
    }

    return sizes.slice().sort(function (a, b) {
      var wa = parseWeight(a);
      var wb = parseWeight(b);
      if (wa !== wb) return wa - wb;
      return a.localeCompare(b);
    });
  }

  /**
   * Format price in paise to INR string using Shopify money conventions.
   * Falls back to a simple division when Shopify's formatMoney isn't available.
   */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    // Remove trailing zeros after decimal for whole numbers
    amount = amount.replace(/\.00$/, '');
    return '₹' + amount;
  }

  function pushDataLayer(event, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, data));
  }

  // ---------------------------------------------------------------------------
  // Web Component
  // ---------------------------------------------------------------------------

  class BundleBuilder extends HTMLElement {
    constructor() {
      super();

      // Parsed catalog from the embedded JSON script tag
      this._catalog = null;

      // Builder state
      this._state = {
        currentStep: 1,
        selectedTier: null,    // { units, percent, label }
        selectedGender: null,  // string
        selectedSize: null,    // string
        slots: [],             // [{ variantId, productId, productTitle, productImage, price, sizeValue }]
        bundleId: generateUUID(),
      };

      this._allowRepeats = true;
    }

    // -------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------

    connectedCallback() {
      var self = this;
      // Defer one tick: connectedCallback fires on the opening tag, before
      // children are parsed, so querySelectorAll would return empty without this.
      setTimeout(function () {
        self._allowRepeats = self.dataset.allowRepeats !== 'false';
        self._parseCatalog();
        self._bindTierCards();
        self._bindCartButton();
      }, 0);
    }

    // -------------------------------------------------------------------------
    // Catalog parsing
    // -------------------------------------------------------------------------

    _parseCatalog() {
      var scriptEl = document.getElementById('bundle-catalog');
      if (!scriptEl) {
        console.warn('[BundleBuilder] #bundle-catalog script tag not found.');
        return;
      }
      try {
        this._catalog = JSON.parse(scriptEl.textContent);
      } catch (e) {
        console.error('[BundleBuilder] Failed to parse catalog JSON:', e);
      }
    }

    // -------------------------------------------------------------------------
    // Step helpers
    // -------------------------------------------------------------------------

    _getStep(n) {
      return this.querySelector('.bb-step[data-step="' + n + '"]');
    }

    _goToStep(n) {
      var steps = this.querySelectorAll('.bb-step');
      steps.forEach(function (s) {
        var stepNum = parseInt(s.dataset.step, 10);
        if (stepNum === n) {
          s.removeAttribute('hidden');
          s.setAttribute('aria-current', 'step');
        } else {
          s.setAttribute('hidden', '');
          s.removeAttribute('aria-current');
        }
      });

      this._state.currentStep = n;
      this._updateStepIndicator(n);
      this._updateRecapStrip();

      // Scroll to top of builder
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    _updateStepIndicator(activeStep) {
      var dots = this.querySelectorAll('.bb-step-dot');
      dots.forEach(function (dot) {
        var step = parseInt(dot.dataset.step, 10);
        dot.classList.toggle('bb-step-dot--active', step === activeStep);
        dot.classList.toggle('bb-step-dot--complete', step < activeStep);
        dot.setAttribute('aria-current', step === activeStep ? 'step' : 'false');
      });
    }

    _updateRecapStrip() {
      var strip = this.querySelector('.bb-recap-strip');
      if (!strip) return;

      var s = this._state;
      if (s.currentStep < 2 || !s.selectedTier) {
        strip.setAttribute('hidden', '');
        return;
      }

      strip.removeAttribute('hidden');

      var tierEl = strip.querySelector('.bb-recap-tier');
      var genderEl = strip.querySelector('.bb-recap-gender');
      var sizeEl = strip.querySelector('.bb-recap-size');

      if (tierEl) tierEl.textContent = s.selectedTier.label;
      if (genderEl) genderEl.textContent = s.selectedGender ? this._genderLabel(s.selectedGender) : '';
      if (sizeEl) sizeEl.textContent = s.selectedSize || '';
    }

    _genderLabel(gender) {
      var map = { girls: 'Girls', boys: 'Boys', babyclan: 'Babyclan', women: 'Women' };
      return map[gender] || gender.charAt(0).toUpperCase() + gender.slice(1);
    }

    _genderAgeRange(gender) {
      var map = { girls: '2–10 Yrs', boys: '2–16 Yrs', babyclan: '0–2 Yrs', women: 'Adults' };
      return map[gender] || '';
    }

    // -------------------------------------------------------------------------
    // Step 1 — Tier selection
    // -------------------------------------------------------------------------

    _bindTierCards() {
      var self = this;
      var cards = this.querySelectorAll('.bb-tier-option');
      cards.forEach(function (card) {
        card.addEventListener('click', function () {
          self._selectTier(card);
        });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self._selectTier(card);
          }
        });
      });
    }

    _selectTier(card) {
      var units = parseInt(card.dataset.units, 10);
      var percent = parseInt(card.dataset.percent, 10);
      var label = card.dataset.label;

      // Visual state
      this.querySelectorAll('.bb-tier-option').forEach(function (c) {
        c.classList.remove('bb-tier-option--selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('bb-tier-option--selected');
      card.setAttribute('aria-checked', 'true');

      // State update
      this._state.selectedTier = { units: units, percent: percent, label: label };
      this._state.slots = [];
      this._state.selectedGender = null;
      this._state.selectedSize = null;

      pushDataLayer('bundle_started', {
        bundle_tier: label,
        bundle_units: units,
        bundle_discount_percent: percent,
      });

      this._initTray();
      this._renderStep2();
      this._goToStep(2);
    }

    // -------------------------------------------------------------------------
    // Step 2 — Gender selection
    // -------------------------------------------------------------------------

    _renderStep2() {
      if (!this._catalog) return;

      // Collect unique genders from catalog styles
      var seenGenders = {};
      var genders = [];
      this._catalog.styles.forEach(function (style) {
        if (!seenGenders[style.gender]) {
          seenGenders[style.gender] = true;
          genders.push(style.gender);
        }
      });

      var grid = this.querySelector('.bb-gender-grid');
      if (!grid) return;

      var self = this;
      grid.innerHTML = '';

      genders.forEach(function (gender) {
        var btn = document.createElement('button');
        btn.className = 'bb-gender-option';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.dataset.gender = gender;
        var ageRange = self._genderAgeRange(gender);
        btn.innerHTML = '<span>' + self._genderLabel(gender) + '</span>' +
          (ageRange ? '<span class="bb-gender-age">' + ageRange + '</span>' : '');

        btn.addEventListener('click', function () {
          self._selectGender(gender, btn);
        });
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self._selectGender(gender, btn);
          }
        });

        grid.appendChild(btn);
      });
    }

    _selectGender(gender, btn) {
      this.querySelectorAll('.bb-gender-option').forEach(function (b) {
        b.classList.remove('bb-gender-option--selected');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('bb-gender-option--selected');
      btn.setAttribute('aria-checked', 'true');

      this._state.selectedGender = gender;
      this._state.selectedSize = null;
      this._state.slots = [];

      this._renderStep3(gender);
      this._goToStep(3);
    }

    // -------------------------------------------------------------------------
    // Step 3 — Size selection
    // -------------------------------------------------------------------------

    _renderStep3(gender) {
      if (!this._catalog) return;

      // Get styles for this gender
      var stylesForGender = this._catalog.styles.filter(function (s) {
        return s.gender === gender;
      });

      // Collect distinct available size values across all style products
      var seenSizes = {};
      var sizeGuideUrl = '';

      stylesForGender.forEach(function (style) {
        if (!sizeGuideUrl && style.sizeGuideUrl) {
          sizeGuideUrl = style.sizeGuideUrl;
        }
        style.products.forEach(function (product) {
          product.variants.forEach(function (variant) {
            if (variant.available && variant.sizeValue) {
              seenSizes[variant.sizeValue] = true;
            }
          });
        });
      });

      var sizes = sortSizes(Object.keys(seenSizes));

      // Update size guide link
      var sizeGuideLink = this.querySelector('.bb-size-guide-link');
      if (sizeGuideLink) {
        if (sizeGuideUrl) {
          sizeGuideLink.href = sizeGuideUrl;
          sizeGuideLink.removeAttribute('hidden');
        } else {
          sizeGuideLink.setAttribute('hidden', '');
        }
      }

      var grid = this.querySelector('.bb-size-grid');
      if (!grid) return;

      var self = this;
      grid.innerHTML = '';

      if (sizes.length === 0) {
        var msg = document.createElement('p');
        msg.className = 'bb-empty-message';
        msg.textContent = 'No sizes available for this category right now.';
        grid.appendChild(msg);
        return;
      }

      sizes.forEach(function (size) {
        var btn = document.createElement('button');
        btn.className = 'bb-size-option';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.dataset.size = size;
        btn.textContent = size;

        btn.addEventListener('click', function () {
          self._selectSize(size, btn);
        });
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self._selectSize(size, btn);
          }
        });

        grid.appendChild(btn);
      });
    }

    _selectSize(size, btn) {
      this.querySelectorAll('.bb-size-option').forEach(function (b) {
        b.classList.remove('bb-size-option--selected');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('bb-size-option--selected');
      btn.setAttribute('aria-checked', 'true');

      this._state.selectedSize = size;
      this._state.slots = [];

      pushDataLayer('bundle_size_selected', {
        bundle_size: size,
        bundle_gender: this._state.selectedGender,
      });

      this._renderStep4();
      this._updateTray();
      this._goToStep(4);
    }

    // -------------------------------------------------------------------------
    // Step 4 — Style tabs + product grid
    // -------------------------------------------------------------------------

    _renderStep4() {
      if (!this._catalog) return;

      var gender = this._state.selectedGender;
      var size = this._state.selectedSize;

      // Get styles for this gender that have at least one product with available variant in selected size
      var stylesForGender = this._catalog.styles.filter(function (style) {
        if (style.gender !== gender) return false;
        return style.products.some(function (product) {
          return product.variants.some(function (v) {
            return v.available && v.sizeValue === size;
          });
        });
      });

      var tabsContainer = this.querySelector('.bb-style-tabs');
      var gridContainer = this.querySelector('.bb-product-grid');
      if (!tabsContainer || !gridContainer) return;

      tabsContainer.innerHTML = '';
      gridContainer.innerHTML = '';

      if (stylesForGender.length === 0) {
        var msg = document.createElement('p');
        msg.className = 'bb-empty-message';
        msg.textContent = 'No styles available for the selected size.';
        gridContainer.appendChild(msg);
        return;
      }

      var self = this;

      // Render tabs
      stylesForGender.forEach(function (style, idx) {
        var tab = document.createElement('button');
        tab.className = 'bb-style-tab' + (idx === 0 ? ' bb-style-tab--active' : '');
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', idx === 0 ? 'true' : 'false');
        tab.setAttribute('aria-controls', 'bb-style-panel-' + style.blockId);
        tab.id = 'bb-style-tab-' + style.blockId;
        tab.dataset.blockId = style.blockId;
        tab.textContent = style.label;

        tab.addEventListener('click', function () {
          self._activateTab(style.blockId, stylesForGender);
        });
        tab.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self._activateTab(style.blockId, stylesForGender);
          }
        });

        tabsContainer.appendChild(tab);
      });

      // Render first tab's products immediately
      this._renderProductsForStyle(stylesForGender[0], gridContainer);
    }

    _activateTab(blockId, stylesForGender) {
      var tabs = this.querySelectorAll('.bb-style-tab');
      tabs.forEach(function (t) {
        var active = t.dataset.blockId === blockId;
        t.classList.toggle('bb-style-tab--active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      var style = stylesForGender.find(function (s) { return s.blockId === blockId; });
      if (!style) return;

      var gridContainer = this.querySelector('.bb-product-grid');
      if (gridContainer) {
        this._renderProductsForStyle(style, gridContainer);
      }
    }

    _renderProductsForStyle(style, container) {
      var size = this._state.selectedSize;
      var tier = this._state.selectedTier;
      var slots = this._state.slots;
      var allowRepeats = this._allowRepeats;
      var self = this;

      container.innerHTML = '';

      // Filter products that have at least one available variant for selected size
      var products = style.products.filter(function (product) {
        return product.variants.some(function (v) {
          return v.available && v.sizeValue === size;
        });
      });

      if (products.length === 0) {
        var msg = document.createElement('p');
        msg.className = 'bb-empty-message';
        msg.textContent = 'No products available in this style for the selected size.';
        container.appendChild(msg);
        return;
      }

      products.forEach(function (product) {
        var variant = product.variants.find(function (v) {
          return v.available && v.sizeValue === size;
        });
        if (!variant) return;

        var card = self._createProductCard(product, variant, tier, slots, allowRepeats);
        container.appendChild(card);
      });
    }

    _createProductCard(product, variant, tier, slots, allowRepeats) {
      var self = this;
      var slotCount = slots.filter(function (s) { return s.variantId === variant.id; }).length;
      var originalPrice = variant.price;
      var discountedPrice = Math.round(originalPrice * (1 - tier.percent / 100));

      var card = document.createElement('div');
      card.className = 'bb-product-card';
      card.dataset.productId = product.id;
      card.dataset.variantId = variant.id;

      var imgWrapper = document.createElement('div');
      imgWrapper.className = 'bb-product-card__img-wrapper';

      var img = document.createElement('img');
      img.className = 'bb-product-card__img';
      img.src = product.image || '';
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      imgWrapper.appendChild(img);

      var info = document.createElement('div');
      info.className = 'bb-product-card__info';

      var title = document.createElement('p');
      title.className = 'bb-product-card__title';
      title.textContent = product.title;

      var priceWrap = document.createElement('div');
      priceWrap.className = 'bb-product-card__price';

      var priceDiscounted = document.createElement('span');
      priceDiscounted.className = 'bb-product-card__price-discounted';
      priceDiscounted.textContent = formatMoney(discountedPrice);

      var priceOriginal = document.createElement('span');
      priceOriginal.className = 'bb-product-card__price-original';
      priceOriginal.textContent = formatMoney(originalPrice);

      priceWrap.appendChild(priceDiscounted);
      priceWrap.appendChild(priceOriginal);

      info.appendChild(title);
      info.appendChild(priceWrap);

      var actions = document.createElement('div');
      actions.className = 'bb-product-card__actions';

      var isFull = self._state.slots.length >= tier.units;

      if (allowRepeats && slotCount > 0) {
        // Show stepper instead of add button
        var stepper = self._createStepper(product, variant, tier);
        actions.appendChild(stepper);
      } else {
        var addBtn = document.createElement('button');
        addBtn.className = 'bb-product-card__add-btn';
        addBtn.textContent = '+ Add';
        addBtn.dataset.variantId = variant.id;

        if (isFull && slotCount === 0) {
          addBtn.disabled = true;
          addBtn.setAttribute('aria-disabled', 'true');
          addBtn.textContent = 'Pack Full';
        }

        addBtn.addEventListener('click', function () {
          self._addToSlots(product, variant, tier);
        });

        actions.appendChild(addBtn);
      }

      card.appendChild(imgWrapper);
      card.appendChild(info);
      card.appendChild(actions);

      return card;
    }

    _createStepper(product, variant, tier) {
      var self = this;
      var slotCount = this._state.slots.filter(function (s) { return s.variantId === variant.id; }).length;
      var isFull = this._state.slots.length >= tier.units;

      var stepper = document.createElement('div');
      stepper.className = 'bb-stepper';
      stepper.setAttribute('aria-label', 'Quantity for ' + product.title);

      var decBtn = document.createElement('button');
      decBtn.className = 'bb-stepper__btn bb-stepper__btn--dec';
      decBtn.textContent = '−';
      decBtn.setAttribute('aria-label', 'Remove one ' + product.title);
      decBtn.addEventListener('click', function () {
        self._removeOneFromSlots(variant.id);
      });

      var countEl = document.createElement('span');
      countEl.className = 'bb-stepper__count';
      countEl.textContent = String(slotCount);
      countEl.setAttribute('aria-live', 'polite');
      countEl.setAttribute('aria-atomic', 'true');

      var incBtn = document.createElement('button');
      incBtn.className = 'bb-stepper__btn bb-stepper__btn--inc';
      incBtn.textContent = '+';
      incBtn.setAttribute('aria-label', 'Add one more ' + product.title);
      if (isFull) {
        incBtn.disabled = true;
        incBtn.setAttribute('aria-disabled', 'true');
      }
      incBtn.addEventListener('click', function () {
        self._addToSlots(product, variant, tier);
      });

      stepper.appendChild(decBtn);
      stepper.appendChild(countEl);
      stepper.appendChild(incBtn);

      return stepper;
    }

    // -------------------------------------------------------------------------
    // Slot management
    // -------------------------------------------------------------------------

    _addToSlots(product, variant, tier) {
      var slots = this._state.slots;
      var units = tier.units;

      if (slots.length >= units) return;

      slots.push({
        variantId: variant.id,
        productId: product.id,
        productTitle: product.title,
        productImage: product.image || '',
        price: variant.price,
        sizeValue: variant.sizeValue || this._state.selectedSize,
      });

      this._updateTray();
      this._refreshProductGrid();
    }

    _removeOneFromSlots(variantId) {
      var slots = this._state.slots;
      // Remove the last occurrence of this variantId
      for (var i = slots.length - 1; i >= 0; i--) {
        if (slots[i].variantId === variantId) {
          slots.splice(i, 1);
          break;
        }
      }
      this._updateTray();
      this._refreshProductGrid();
    }

    _removeSlotByIndex(index) {
      this._state.slots.splice(index, 1);
      this._updateTray();
      this._refreshProductGrid();
    }

    _refreshProductGrid() {
      if (this._state.currentStep !== 4 || !this._catalog) return;

      var gender = this._state.selectedGender;
      var tier = this._state.selectedTier;

      var stylesForGender = this._catalog.styles.filter(function (style) {
        if (style.gender !== gender) return false;
        return style.products.some(function (product) {
          return product.variants.some(function (v) {
            return v.available && v.sizeValue;
          });
        });
      });

      var activeTab = this.querySelector('.bb-style-tab--active');
      if (!activeTab) return;

      var activeBlockId = activeTab.dataset.blockId;
      var activeStyle = stylesForGender.find(function (s) { return s.blockId === activeBlockId; });
      if (!activeStyle) return;

      var gridContainer = this.querySelector('.bb-product-grid');
      if (gridContainer) {
        this._renderProductsForStyle(activeStyle, gridContainer);
      }
    }

    // -------------------------------------------------------------------------
    // Tray
    // -------------------------------------------------------------------------

    _initTray() {
      var tier = this._state.selectedTier;
      if (!tier) return;

      var slotsContainer = this.querySelector('.bb-tray-slots');
      if (!slotsContainer) return;

      slotsContainer.innerHTML = '';

      for (var i = 0; i < tier.units; i++) {
        var circle = document.createElement('div');
        circle.className = 'bb-slot-circle bb-slot-circle--empty';
        circle.setAttribute('role', 'listitem');
        circle.setAttribute('aria-label', 'Empty slot ' + (i + 1));
        slotsContainer.appendChild(circle);
      }

      this._updateTray();
    }

    _updateTray() {
      var tier = this._state.selectedTier;
      if (!tier) return;

      var slots = this._state.slots;
      var units = tier.units;
      var self = this;

      // Update slot circles
      var slotsContainer = this.querySelector('.bb-tray-slots');
      if (slotsContainer) {
        var circles = slotsContainer.querySelectorAll('.bb-slot-circle');
        circles.forEach(function (circle, idx) {
          var slot = slots[idx];
          // Remove previous remove button if any
          circle.innerHTML = '';

          if (slot) {
            circle.className = 'bb-slot-circle bb-slot-circle--filled';
            circle.setAttribute('aria-label', slot.productTitle + ', slot ' + (idx + 1));

            var img = document.createElement('img');
            img.src = slot.productImage || '';
            img.alt = slot.productTitle;
            img.loading = 'lazy';
            img.className = 'bb-slot-circle__img';

            var removeBtn = document.createElement('button');
            removeBtn.className = 'bb-slot-circle__remove';
            removeBtn.setAttribute('aria-label', 'Remove ' + slot.productTitle + ' from slot ' + (idx + 1));
            removeBtn.innerHTML = '&times;';
            removeBtn.addEventListener('click', function (e) {
              e.stopPropagation();
              self._removeSlotByIndex(idx);
            });

            circle.appendChild(img);
            circle.appendChild(removeBtn);
          } else {
            circle.className = 'bb-slot-circle bb-slot-circle--empty';
            circle.setAttribute('aria-label', 'Empty slot ' + (idx + 1));
          }
        });
      }

      // Counter text
      var counter = this.querySelector('.bb-tray-counter');
      if (counter) {
        var remaining = units - slots.length;
        if (remaining > 0) {
          counter.textContent = 'Add ' + remaining + ' more';
        } else {
          counter.textContent = 'Pack complete!';
        }
      }

      // Pricing
      var pricingEl = this.querySelector('.bb-tray-pricing');
      var priceOriginalEl = this.querySelector('.bb-price-original');
      var priceDiscountedEl = this.querySelector('.bb-price-discounted');
      var discountBadgeEl = this.querySelector('.bb-discount-badge');

      if (slots.length > 0 && pricingEl) {
        pricingEl.removeAttribute('hidden');

        var totalOriginal = slots.reduce(function (sum, s) { return sum + s.price; }, 0);
        var totalDiscounted = Math.round(totalOriginal * (1 - tier.percent / 100));

        if (priceOriginalEl) priceOriginalEl.textContent = formatMoney(totalOriginal);
        if (priceDiscountedEl) priceDiscountedEl.textContent = formatMoney(totalDiscounted);
        if (discountBadgeEl) discountBadgeEl.textContent = tier.percent + '% off';
      } else if (pricingEl) {
        pricingEl.setAttribute('hidden', '');
      }

      // Checkout button
      var checkoutBtn = this.querySelector('.bb-add-to-cart');
      if (checkoutBtn) {
        var isFull = slots.length === units;
        checkoutBtn.disabled = !isFull;
        checkoutBtn.setAttribute('aria-disabled', isFull ? 'false' : 'true');
      }
    }

    // -------------------------------------------------------------------------
    // Cart submission
    // -------------------------------------------------------------------------

    _bindCartButton() {
      var self = this;
      var btn = this.querySelector('.bb-add-to-cart');
      if (!btn) return;

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        self._submitCart();
      });
    }

    _submitCart() {
      var self = this;
      var tier = this._state.selectedTier;
      var slots = this._state.slots;
      var bundleId = this._state.bundleId;

      if (!tier || slots.length !== tier.units) return;

      var btn = this.querySelector('.bb-add-to-cart');
      var errorEl = this.querySelector('.bb-tray-error');

      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.textContent = 'Adding…';
      }

      if (errorEl) {
        errorEl.setAttribute('hidden', '');
        errorEl.textContent = '';
      }

      var items = slots.map(function (slot) {
        return {
          id: slot.variantId,
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            _bundle_size: String(tier.units),
          },
        };
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (data) {
              throw new Error(data.description || data.message || 'Could not add to cart. Please try again.');
            });
          }
          return res.json();
        })
        .then(function () {
          var totalOriginal = slots.reduce(function (sum, s) { return sum + s.price; }, 0);
          var totalDiscounted = Math.round(totalOriginal * (1 - tier.percent / 100));

          pushDataLayer('bundle_pack_added', {
            bundle_id: bundleId,
            bundle_tier: tier.label,
            bundle_units: tier.units,
            bundle_discount_percent: tier.percent,
            bundle_value_original: totalOriginal / 100,
            bundle_value_discounted: totalDiscounted / 100,
            currency: 'INR',
          });

          window.location.href = '/checkout';
        })
        .catch(function (err) {
          if (btn) {
            btn.disabled = false;
            btn.setAttribute('aria-disabled', 'false');
            btn.textContent = 'Checkout Now';
          }

          if (errorEl) {
            errorEl.removeAttribute('hidden');
            errorEl.textContent = err.message || 'Something went wrong. Please try again.';
          }

          console.error('[BundleBuilder] Cart add failed:', err);
        });
    }
  }

  // Register the custom element
  if (!customElements.get('bundle-builder')) {
    customElements.define('bundle-builder', BundleBuilder);
  }
})();
